import { useEffect, useState } from "react";

/**
 * Big circular SVG gauge.
 * value: 0-100
 * color: hex/css color string
 */
export default function CircularGauge({ value = 0, color = "#22c55e", label = "" }) {
  const safe = Math.max(0, Math.min(100, value));
  const [display, setDisplay] = useState(0);

  // Geometry
  const SIZE = 240;
  const STROKE = 18;
  const R = (SIZE - STROKE) / 2;
  const C = 2 * Math.PI * R;
  const offset = C - (display / 100) * C;

  useEffect(() => {
    // Animate number count
    let start = null;
    const duration = 1500;
    const from = 0;
    const to = safe;
    const step = (t) => {
      if (!start) start = t;
      const progress = Math.min(1, (t - start) / duration);
      // ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (progress < 1) requestAnimationFrame(step);
    };
    const id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [safe]);

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: SIZE, height: SIZE }}
      data-testid="circular-gauge"
    >
      {/* Soft glow behind */}
      <div
        className="absolute inset-4 rounded-full blur-2xl opacity-50"
        style={{ background: color }}
        aria-hidden
      />

      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="relative -rotate-90"
      >
        {/* Track */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          stroke="rgba(255,255,255,0.07)"
          strokeWidth={STROKE}
          fill="none"
        />
        {/* Progress */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          stroke={color}
          strokeWidth={STROKE}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={C}
          strokeDashoffset={offset}
          className="vx-ring-progress"
          style={{
            filter: `drop-shadow(0 0 14px ${color})`,
          }}
        />
      </svg>

      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div
            className="font-display font-black leading-none vx-count-tabular"
            style={{
              color,
              fontSize: SIZE * 0.32,
              textShadow: `0 0 30px ${color}`,
            }}
            data-testid="gauge-value"
          >
            {display}
            <span style={{ fontSize: SIZE * 0.13, marginLeft: 4 }}>%</span>
          </div>
          {label && (
            <div className="mt-1 text-[10px] uppercase tracking-[0.25em] font-mono text-[--vx-text-dim]">
              {label}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
